import { useEffect, useState } from "react"

import { fetchClientCard } from "../../api/clients.api"
import { formatDayMonth } from "../../lib/format"

/**
 * Дата согласия клиента на обработку данных — для шага «Итог» (5-А2).
 *
 * ЗАЧЕМ ОНА НА ЭКРАНЕ. Это ответ на вопрос, который задают не в спокойный день:
 * на каком основании салон хранит номер этого человека. Администратор, записывающий
 * постоянного клиента, видит «согласие получено 12 марта» и не переспрашивает;
 * если даты нет — видит, что согласие запишется сейчас, с его слов.
 *
 * ОТДЕЛЬНЫЙ ЗАПРОС, И ТОЛЬКО НА ПОСЛЕДНЕМ ШАГЕ. Поиск клиента отдаёт строку списка
 * (ClientListItem), согласий в ней нет и быть не должно: список клиентов не место
 * для юридических отметок. Карточка тянется один раз и только тогда, когда её
 * собрались показать, — на первом шаге этот запрос был бы платой за каждую цифру
 * набранного номера.
 *
 * ОТКАЗ МОЛЧИТ. Не удалось прочитать карточку — шаг просто не называет дату: писать
 * «согласия нет» там, где оно, возможно, есть, нельзя, а мешать записи из-за справки
 * тем более.
 */
export function useConsentDate(input: {
  readonly clientId: string | null
  readonly active: boolean
  readonly timeZone: string
}): string | null {
  const { clientId, active, timeZone } = input
  const [granted, setGranted] = useState<{ id: string; label: string } | null>(null)

  useEffect(() => {
    if (!active || clientId === null) return
    if (granted?.id === clientId) return
    const controller = new AbortController()
    void (async () => {
      try {
        const card = await fetchClientCard(clientId, controller.signal)
        if (controller.signal.aborted) return
        const consent = card.consents.find(
          (item) => item.kind === "pd" && item.revokedAt === null,
        )
        if (consent === undefined) return
        setGranted({ id: clientId, label: formatDayMonth(consent.grantedAt, timeZone) })
      } catch {
        // Молча: справка о согласии не повод мешать записи.
      }
    })()
    return () => {
      controller.abort()
    }
  }, [clientId, active, timeZone, granted])

  return granted?.id === clientId ? granted.label : null
}
